/**
 * Página de Gestión de Miembros
 * - Unión: Ve estadísticas agregadas por asociación
 * - Asociación: Ve miembros de su ámbito (solo lectura)
 * - Club Director: Gestión completa de miembros de su club
 */

import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Input,
  Select,
  RoleBadge,
  SectionBadge,
} from "@/components/ui";
import {
  Users,
  Search,
  Edit,
  Trash2,
  Eye,
  Loader2,
  UserPlus,
  X,
  Building2,
  TrendingUp,
  UserCheck,
  Inbox,
  ArrowRight,
  Plus,
  Sparkles,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Shield,
  ChevronDown,
  SlidersHorizontal,
  MoreVertical,
  Award,
  Camera,
  FileWarning,
  Cake,
  CheckCircle2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  useScopedMembers,
  useDeleteMember,
  useHardDeleteMember,
  useReactivateMember,
} from "@/hooks/usePersons";
import { usePermissions } from "@/hooks/usePermissions";
import { useAssociations, useClubs } from "@/hooks/useOrganization";
import { useActiveAssignmentsByClub } from "@/hooks/useMemberRoles";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { Can } from "@/components/auth/Can";
import { joinRequestService } from "@/lib/services/joinRequests";
import { profileUpdateRequestService } from "@/lib/services/profileUpdateRequests";
import MemberModal from "./components/MemberModal";
import MemberDetailModal from "./components/MemberDetailModal";
import type {
  MemberWithPerson as Member,
  MemberRoleAssignment,
  MemberRole,
  ClubType,
} from "@/types";
import {
  MEMBER_ROLE_LABELS,
  MEMBER_CLASS_LABELS,
  CLUB_TYPE_LABELS,
} from "@/types";
import { exportToXlsx } from "@/lib/utils/exportXlsx";

const statusLabels: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
};

// ============================================
// VISTA DE MIEMBROS PARA UNIÓN (Solo estadísticas)
// ============================================
function UnionMembersView() {
  const { data: members, isLoading } = useScopedMembers();
  const { data: associations } = useAssociations();
  const { data: clubs } = useAccessibleClubs();
  const [selectedAssociationId, setSelectedAssociationId] =
    useState<string>("all");

  // Estadísticas generales
  const stats = useMemo(() => {
    if (!members) return { total: 0, active: 0, inactive: 0 };

    return {
      total: members.length,
      active: members.filter((m) => m.status === "active").length,
      inactive: members.filter((m) => m.status !== "active").length,
    };
  }, [members]);

  // Estadísticas por asociación (usando associationId de Member)
  const statsByAssociation = useMemo(() => {
    if (!members || !clubs || !associations) return [];

    return associations.map((assoc) => {
      // Filtrar miembros que pertenecen a esta asociación
      const assocMembers = members.filter((m) => m.associationId === assoc.id);

      // Contar clubs de esta asociación
      const clubCount = clubs.filter(
        (c) => c.associationId === assoc.id,
      ).length;

      return {
        id: assoc.id,
        name: assoc.name,
        region: assoc.region,
        totalMembers: assocMembers.length,
        activeMembers: assocMembers.filter((m) => m.status === "active").length,
        clubCount,
      };
    });
  }, [members, clubs, associations]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Users className="w-5 h-5 text-white" />
            </div>
            Estadísticas de Miembros
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Vista general de miembros por asociación
          </p>
        </div>

        {/* Filtro de Asociación */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Filtrar:</label>
          <select
            value={selectedAssociationId}
            onChange={(e) => setSelectedAssociationId(e.target.value)}
            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl bg-white focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 text-sm transition-all duration-200 hover:border-gray-300"
          >
            <option value="all">Todas las asociaciones</option>
            {associations?.map((assoc) => (
              <option key={assoc.id} value={assoc.id}>
                {assoc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-blue-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Miembros
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.total}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-green-50/50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Activos</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.active}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md bg-gradient-to-br from-white to-gray-50">
          <CardContent className="py-5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Inactivos</p>
                <p className="text-3xl font-bold text-gray-600 mt-1">
                  {stats.inactive}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/30 group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla por Asociación */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-100 pb-3">
          <CardTitle className="text-lg flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            Miembros por Asociación
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {statsByAssociation.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Asociación
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Clubes
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Total Miembros
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      Activos
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                      % Activos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {statsByAssociation
                    .filter(
                      (a) =>
                        selectedAssociationId === "all" ||
                        a.id === selectedAssociationId,
                    )
                    .map((assoc) => {
                      const activePercent =
                        assoc.totalMembers > 0
                          ? Math.round(
                              (assoc.activeMembers / assoc.totalMembers) * 100,
                            )
                          : 0;

                      return (
                        <tr
                          key={assoc.id}
                          className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors duration-150"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                                <Building2 className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {assoc.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {assoc.region || "Sin región"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className="bg-green-100 text-green-700">
                              {assoc.clubCount}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="font-semibold text-gray-900">
                              {assoc.totalMembers}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-green-600 font-medium">
                              {assoc.activeMembers}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 rounded-full"
                                  style={{ width: `${activePercent}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">
                                {activePercent}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                {selectedAssociationId === "all" && (
                  <tfoot>
                    <tr className="bg-gray-50 font-medium">
                      <td className="py-3 px-4 text-gray-900">Total General</td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {statsByAssociation.reduce(
                          (sum, a) => sum + a.clubCount,
                          0,
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-900">
                        {statsByAssociation.reduce(
                          (sum, a) => sum + a.totalMembers,
                          0,
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-green-600">
                        {statsByAssociation.reduce(
                          (sum, a) => sum + a.activeMembers,
                          0,
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">-</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Users className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium">
                No hay datos de miembros disponibles
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nota informativa */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25 flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Vista de Unión:</strong> Aquí puedes ver estadísticas
                agregadas de miembros. La gestión de miembros individuales se
                realiza a nivel de club por los directores.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// VISTA DE MIEMBROS PARA ASOCIACIÓN (Lista completa con filtros)
// ============================================

type AssociationSortKey = "name" | "club" | "role" | "class" | "age" | "status";

function SortableTh({
  label,
  sortKey,
  sortBy,
  sortDir,
  onClick,
  align = "left",
}: {
  label: string;
  sortKey: AssociationSortKey;
  sortBy: AssociationSortKey;
  sortDir: "asc" | "desc";
  onClick: (k: AssociationSortKey) => void;
  align?: "left" | "center" | "right";
}) {
  const active = sortBy === sortKey;
  const alignCls =
    align === "center"
      ? "justify-center"
      : align === "right"
        ? "justify-end"
        : "justify-start";
  const thAlign =
    align === "center"
      ? "text-center"
      : align === "right"
        ? "text-right"
        : "text-left";
  return (
    <th className={`py-3 px-4 font-medium text-gray-600 text-sm ${thAlign}`}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 select-none hover:text-gray-900 transition-colors ${alignCls} ${active ? "text-gray-900" : ""}`}
      >
        <span>{label}</span>
        {active ? (
          sortDir === "asc" ? (
            <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
          )
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
        )}
      </button>
    </th>
  );
}

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-100">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:bg-primary-100 rounded-full p-0.5 transition-colors"
        aria-label={`Quitar filtro ${label}`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

/** Chip preset rápido — combinación de filtros comunes con 1 click. */
function QuickPreset({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all whitespace-nowrap ${
        active
          ? "bg-primary-600 text-white border-primary-600 shadow-sm"
          : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

const DOC_FILTER_LABELS: Record<string, string> = {
  "no-photo": "Sin foto",
  "no-doc": "Sin documento",
  "no-eps": "Sin EPS",
  incomplete: "Incompleta",
  complete: "Completa",
};

function AssociationMembersView() {
  const { data: members, isLoading } = useScopedMembers();
  const { data: clubs } = useAccessibleClubs();
  const { profile } = useAuthStore();
  const associationName = profile?.scopeName ?? "tu Asociación";

  const [searchTerm, setSearchTerm] = useState("");
  const [clubFilter, setClubFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [classFilter, setClassFilter] = useState<string>("all");

  // Ordenamiento de la tabla
  const [sortBy, setSortBy] = useState<AssociationSortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Paginación (cliente)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  // Obtener nombre del club
  const getClubName = (clubId: string) => {
    const club = clubs?.find((c) => c.id === clubId);
    return club?.name || "Club";
  };

  // Estadísticas generales
  const stats = useMemo(() => {
    if (!members) return { total: 0, active: 0, byClub: 0 };
    return {
      total: members.length,
      active: members.filter((m) => m.status === "active").length,
      byClub: clubs?.length || 0,
    };
  }, [members, clubs]);

  // Filtrar miembros
  const filteredMembers = useMemo(() => {
    if (!members) return [];

    return members.filter((member) => {
      // Filtro de búsqueda
      const searchMatch =
        searchTerm === "" ||
        member.person.fullName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        member.person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.person.idDocumentNumber?.includes(searchTerm) ||
        getClubName(member.clubId)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtro de club
      const clubMatch = clubFilter === "all" || member.clubId === clubFilter;

      // Filtro de estado
      const statusMatch =
        statusFilter === "all" || member.status === statusFilter;

      // Filtro de rol
      const roleMatch = roleFilter === "all" || member.role === roleFilter;

      // Filtro de clase
      const classMatch = classFilter === "all" || member.clase === classFilter;

      return searchMatch && clubMatch && statusMatch && roleMatch && classMatch;
    });
  }, [
    members,
    searchTerm,
    clubFilter,
    statusFilter,
    roleFilter,
    classFilter,
    clubs,
  ]);

  const handleView = (member: Member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setClubFilter("all");
    setStatusFilter("all");
    setRoleFilter("all");
    setClassFilter("all");
  };

  const hasActiveFilters =
    searchTerm !== "" ||
    clubFilter !== "all" ||
    statusFilter !== "all" ||
    roleFilter !== "all" ||
    classFilter !== "all";

  const calculateAge = (
    birthDate: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!birthDate) return "-";
    const date =
      typeof birthDate === "object" && "toDate" in birthDate && birthDate.toDate
        ? birthDate.toDate()
        : new Date(birthDate as unknown as string | number | Date);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      age--;
    }
    return `${age} años`;
  };

  // Edad como número (-1 si no hay fecha) — usado para ordenar.
  const ageNumber = (
    birthDate: { toDate?: () => Date } | Date | null | undefined,
  ): number => {
    if (!birthDate) return -1;
    const date =
      typeof birthDate === "object" && "toDate" in birthDate && birthDate.toDate
        ? birthDate.toDate()
        : new Date(birthDate as unknown as string | number | Date);
    if (isNaN(date.getTime())) return -1;
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Reset de página al cambiar filtros / orden / tamaño
  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    clubFilter,
    statusFilter,
    roleFilter,
    classFilter,
    sortBy,
    sortDir,
    pageSize,
  ]);

  const handleSort = (key: AssociationSortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  // Aplicar ordenamiento sobre los miembros filtrados
  const sortedMembers = useMemo(() => {
    const arr = [...filteredMembers];
    const dir = sortDir === "asc" ? 1 : -1;
    const collator = new Intl.Collator("es", { sensitivity: "base" });
    arr.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortBy) {
        case "name":
          av = a.person.fullName ?? "";
          bv = b.person.fullName ?? "";
          break;
        case "club":
          av = getClubName(a.clubId);
          bv = getClubName(b.clubId);
          break;
        case "role":
          av = (a.role && MEMBER_ROLE_LABELS[a.role]) || a.role || "";
          bv = (b.role && MEMBER_ROLE_LABELS[b.role]) || b.role || "";
          break;
        case "class":
          av = (a.clase && MEMBER_CLASS_LABELS[a.clase]) || a.clase || "";
          bv = (b.clase && MEMBER_CLASS_LABELS[b.clase]) || b.clase || "";
          break;
        case "age":
          av = ageNumber(a.person.birthDate);
          bv = ageNumber(b.person.birthDate);
          break;
        case "status":
          av = statusLabels[a.status] || a.status;
          bv = statusLabels[b.status] || b.status;
          break;
      }
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return collator.compare(String(av), String(bv)) * dir;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMembers, sortBy, sortDir, clubs]);

  const totalResults = sortedMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, totalResults);
  const paginatedMembers = useMemo(
    () => sortedMembers.slice(pageStart, pageEnd),
    [sortedMembers, pageStart, pageEnd],
  );

  // Exportar TODOS los resultados filtrados (no solo la página) a Excel
  const exportFilteredToCSV = () => {
    if (sortedMembers.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    exportToXlsx<Member>({
      filename: `miembros_${stamp}`,
      sheetName: "Miembros",
      rows: sortedMembers,
      columns: [
        { header: "Nombre", key: (m) => m.person.fullName ?? "", width: 28 },
        { header: "Email", key: (m) => m.person.email ?? "", width: 28 },
        {
          header: "Documento",
          key: (m) => m.person.idDocumentNumber ?? "",
          width: 16,
        },
        { header: "Club", key: (m) => getClubName(m.clubId), width: 24 },
        {
          header: "Rol",
          key: (m) => (m.role && MEMBER_ROLE_LABELS[m.role]) || m.role || "",
          width: 18,
        },
        {
          header: "Clase",
          key: (m) =>
            (m.clase && MEMBER_CLASS_LABELS[m.clase]) || m.clase || "",
          width: 16,
        },
        {
          header: "Edad",
          key: (m) => {
            const n = ageNumber(m.person.birthDate);
            return n >= 0 ? n : "";
          },
          width: 8,
        },
        {
          header: "Estado",
          key: (m) => statusLabels[m.status] || m.status,
          width: 14,
        },
      ],
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header compacto con stats inline */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Users className="w-5 h-5 text-white" />
            </div>
            Miembros — {associationName}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5 ml-[52px]">
            Consulta y busca miembros de todos los clubes de tu asociación
          </p>
        </div>

        {/* Stats compactos en línea */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl border border-blue-100">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              {stats.total} miembros
            </span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-green-100/50 rounded-xl border border-green-100">
            <UserCheck className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-green-900">
              {stats.active} activos
            </span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-xl border border-purple-100">
            <Building2 className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-900">
              {stats.byClub} clubes
            </span>
          </div>
        </div>
      </div>

      {/* Onboarding cuando no hay clubes aún */}
      {stats.byClub === 0 ? (
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardContent className="py-16 px-6">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-accent-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary-500/30">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Empieza por agregar tus clubes
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Tu asociación aún no tiene clubes registrados. Una vez que
                tengas clubes con sus directores, los miembros aparecerán
                automáticamente aquí.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/organization">
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Crear o aprobar clubes
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/organization/club-requests">
                  <Button variant="outline" className="flex items-center gap-2">
                    <Inbox className="w-4 h-4" />
                    Ver solicitudes de clubes
                  </Button>
                </Link>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-900 mb-1">
                    1. Crear clubes
                  </p>
                  <p className="text-xs text-blue-700">
                    Registra los clubes de tu asociación.
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-purple-50 border border-purple-100">
                  <p className="text-xs font-semibold text-purple-900 mb-1">
                    2. Asignar directores
                  </p>
                  <p className="text-xs text-purple-700">
                    Cada club necesita un director responsable.
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-green-50 border border-green-100">
                  <p className="text-xs font-semibold text-green-900 mb-1">
                    3. Inscribir miembros
                  </p>
                  <p className="text-xs text-green-700">
                    Los directores agregan a sus miembros.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : stats.total === 0 ? (
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardContent className="py-16 px-6">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-500/30">
                <UserPlus className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Aún no hay miembros inscritos
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Tienes {stats.byClub} club{stats.byClub === 1 ? "" : "es"} pero
                ningún miembro inscrito todavía. Los directores pueden agregar
                miembros desde la gestión de su club.
              </p>
              <Link to="/organization">
                <Button variant="outline" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Ver clubes de la asociación
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Card única con búsqueda, filtros y tabla */
        <Card className="border-0 shadow-lg overflow-hidden">
          {/* Barra de búsqueda y filtros */}
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
            <div className="flex flex-col gap-3">
              {/* Primera fila: Búsqueda + acciones */}
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    placeholder="Buscar por nombre, email, documento o club..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  <span className="hidden sm:inline text-sm text-gray-500 whitespace-nowrap">
                    <span className="font-semibold text-gray-800">
                      {filteredMembers.length}
                    </span>{" "}
                    de {stats.total}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportFilteredToCSV}
                    disabled={sortedMembers.length === 0}
                    className="h-10"
                    title="Exportar a Excel los resultados filtrados"
                  >
                    <Download className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Exportar</span>
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-10 text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Limpiar</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Segunda fila: Filtros en grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Club
                  </label>
                  <Select
                    value={clubFilter}
                    onChange={(e) => setClubFilter(e.target.value)}
                    className="h-10 text-sm"
                  >
                    <option value="all">Todos los clubes</option>
                    {clubs?.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Estado
                  </label>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 text-sm"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Cargo
                  </label>
                  <Select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="h-10 text-sm"
                  >
                    <option value="all">Todos los cargos</option>
                    <option value="director">Director</option>
                    <option value="subdirector">Subdirector</option>
                    <option value="secretary">Secretario</option>
                    <option value="treasurer">Tesorero</option>
                    <option value="counselor">Consejero</option>
                    <option value="instructor">Instructor</option>
                    <option value="member">Miembro</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Clase
                  </label>
                  <Select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    className="h-10 text-sm"
                  >
                    <option value="all">Todas las clases</option>
                    <option value="amigo">Amigo</option>
                    <option value="companero">Compañero</option>
                    <option value="explorador">Explorador</option>
                    <option value="orientador">Orientador</option>
                    <option value="viajero">Viajero</option>
                    <option value="guia">Guía</option>
                    <option value="guia_mayor">Guía Mayor</option>
                  </Select>
                </div>
              </div>

              {/* Chips de filtros activos */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  {searchTerm && (
                    <FilterChip
                      label={`Búsqueda: "${searchTerm}"`}
                      onRemove={() => setSearchTerm("")}
                    />
                  )}
                  {clubFilter !== "all" && (
                    <FilterChip
                      label={`Club: ${getClubName(clubFilter)}`}
                      onRemove={() => setClubFilter("all")}
                    />
                  )}
                  {statusFilter !== "all" && (
                    <FilterChip
                      label={`Estado: ${statusLabels[statusFilter] || statusFilter}`}
                      onRemove={() => setStatusFilter("all")}
                    />
                  )}
                  {roleFilter !== "all" && (
                    <FilterChip
                      label={`Rol: ${MEMBER_ROLE_LABELS[roleFilter as keyof typeof MEMBER_ROLE_LABELS] || roleFilter}`}
                      onRemove={() => setRoleFilter("all")}
                    />
                  )}
                  {classFilter !== "all" && (
                    <FilterChip
                      label={`Clase: ${MEMBER_CLASS_LABELS[classFilter as keyof typeof MEMBER_CLASS_LABELS] || classFilter}`}
                      onRemove={() => setClassFilter("all")}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabla de miembros */}
          <CardContent className="p-0">
            {filteredMembers.length > 0 ? (
              <div className="overflow-auto max-h-[calc(100vh-380px)] min-h-[300px]">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <SortableTh
                        label="Miembro"
                        sortKey="name"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="left"
                      />
                      <SortableTh
                        label="Club"
                        sortKey="club"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="left"
                      />
                      <SortableTh
                        label="Rol"
                        sortKey="role"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="left"
                      />
                      <SortableTh
                        label="Clase"
                        sortKey="class"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="left"
                      />
                      <SortableTh
                        label="Edad"
                        sortKey="age"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="center"
                      />
                      <SortableTh
                        label="Estado"
                        sortKey="status"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onClick={handleSort}
                        align="center"
                      />
                      <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedMembers.map((member) => (
                      <tr
                        key={member.id}
                        onClick={() => handleView(member)}
                        className="hover:bg-purple-50/40 cursor-pointer transition-colors duration-200"
                      >
                        {/* Miembro: Foto + Nombre + Email combinados */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-md shadow-primary-500/25">
                              {member.person.photoUrl ? (
                                <img
                                  src={member.person.photoUrl}
                                  alt={member.person.fullName}
                                  className="w-9 h-9 rounded-xl object-cover"
                                />
                              ) : (
                                member.person.fullName?.charAt(0) || "?"
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {member.person.fullName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {member.person.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {getClubName(member.clubId)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            const sec = member.staffSection ?? member.section;
                            const isMember = member.role === "member";
                            const cross =
                              member.staffSection &&
                              member.staffSection !== member.section;
                            const labels = CLUB_TYPE_LABELS as Record<
                              string,
                              string
                            >;
                            return (
                              <>
                                {isMember && !member.staffSection ? (
                                  member.section ? (
                                    <SectionBadge
                                      section={member.section}
                                      variant="soft"
                                      size="xs"
                                    />
                                  ) : (
                                    <span className="text-sm text-gray-700">
                                      -
                                    </span>
                                  )
                                ) : (
                                  <RoleBadge
                                    role={member.role}
                                    section={sec ?? undefined}
                                    showSection={!!sec}
                                    size="xs"
                                  />
                                )}
                                {cross && member.section && (
                                  <div className="mt-0.5 text-xs">
                                    <span className="text-gray-400">
                                      {labels[member.section] ?? member.section}{" "}
                                      por edad
                                    </span>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-700">
                            {member.clase
                              ? MEMBER_CLASS_LABELS[member.clase] ||
                                member.clase
                              : "-"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-sm text-gray-600">
                            {calculateAge(member.person.birthDate)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge
                            className={`text-xs ${statusColors[member.status] || "bg-gray-100"}`}
                          >
                            {statusLabels[member.status] || member.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(member);
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2 transition-colors duration-200"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="w-8 h-8 text-gray-500" />
                </div>
                <p className="font-medium text-gray-600">
                  No se encontraron miembros
                </p>
                <p className="text-sm mt-1 text-gray-500">
                  {hasActiveFilters
                    ? "Intenta ajustar los filtros de búsqueda"
                    : "No hay miembros registrados en la asociación"}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="mt-3 rounded-xl"
                  >
                    Limpiar filtros
                  </Button>
                )}
              </div>
            )}
          </CardContent>
          {/* Footer de paginación */}
          {filteredMembers.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/60">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Mostrando{" "}
                  <span className="font-semibold text-gray-800">
                    {totalResults === 0 ? 0 : pageStart + 1}
                  </span>
                  –
                  <span className="font-semibold text-gray-800">{pageEnd}</span>{" "}
                  de{" "}
                  <span className="font-semibold text-gray-800">
                    {totalResults}
                  </span>
                </span>
                <span className="text-gray-300">•</span>
                <label className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Por página:</span>
                  <Select
                    value={String(pageSize)}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="text-xs h-7 py-0 w-auto"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </Select>
                </label>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={safePage === 1}
                  className="h-8 w-8 p-0"
                  title="Primera página"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="h-8 w-8 p-0"
                  title="Anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-700 px-2 select-none">
                  Página <span className="font-semibold">{safePage}</span> de{" "}
                  <span className="font-semibold">{totalPages}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="h-8 w-8 p-0"
                  title="Siguiente"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={safePage === totalPages}
                  className="h-8 w-8 p-0"
                  title="Última página"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Modal de detalle */}
      {selectedMember && (
        <MemberDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          onEdit={() => {}}
        />
      )}
    </div>
  );
}

// ============================================
// ============================================
// ============================================
// Chip de cargos adicionales de un miembro (multi-cargo)
// ============================================
// Recibe todas las asignaciones activas del miembro y las filtra para mostrar
// solo las que NO son el cargo principal (comparando por clubId/section/role).
// Si no hay cargos extra retorna null.
function MemberExtraRoles({
  assignments,
  principalRole,
  principalSection,
  principalClubId,
}: {
  assignments: MemberRoleAssignment[];
  principalRole: MemberRole;
  principalSection: ClubType | undefined;
  principalClubId?: string;
}) {
  const extras = assignments.filter((a) => {
    const sameClub = a.clubId === principalClubId;
    const sameSection = a.section === principalSection;
    const sameRole = a.role === principalRole;
    return !(sameClub && sameSection && sameRole);
  });
  if (extras.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1 mt-1"
      role="group"
      aria-label="Cargos adicionales"
      title="Cargos adicionales"
    >
      <span className="sr-only">Cargos adicionales:</span>
      {extras.map((a) => (
        <RoleBadge
          key={a.id}
          role={a.role}
          section={a.section}
          showSection
          size="xs"
          variant="soft"
        />
      ))}
    </div>
  );
}

// ============================================
// VISTA DE GESTIÓN DE MIEMBROS (Club Director)
// ============================================
function ClubMembersView() {
  const { profile } = useAuthStore();
  const clubId = profile?.directorOfClub?.id;
  const { directorOfSection } = usePermissions();
  const { data: members, isLoading } = useScopedMembers();
  const { data: clubs } = useClubs();
  const { data: clubAssignments } = useActiveAssignmentsByClub(clubId);
  const deleteMutation = useDeleteMember();
  const hardDeleteMutation = useHardDeleteMember();
  const reactivateMutation = useReactivateMember();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  // Filtro por sección/tipo de club. Si el usuario es director de sección
  // arrancamos con SU sección preseleccionada (comportamiento útil por
  // defecto), pero puede pasarlo a "Todos" para ver todo el club.
  const [sectionFilter, setSectionFilter] = useState<string>(
    directorOfSection ?? "all",
  );
  // Filtros nuevos: documentación, cumpleaños, staff/miembro, orden.
  const [docFilter, setDocFilter] = useState<
    "all" | "no-photo" | "no-doc" | "no-eps" | "incomplete" | "complete"
  >("all");
  const [birthdayThisMonth, setBirthdayThisMonth] = useState(false);
  const [staffOnly, setStaffOnly] = useState(false);
  const [sortBy, setSortBy] = useState<"name" | "age" | "recent" | "docPct">(
    "name",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  // BUG-002: Hard delete (irreversible). Requiere checkbox + escribir nombre.
  const [hardDeleteMode, setHardDeleteMode] = useState(false);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState("");
  // Panel de filtros colapsable en mobile (siempre visible en sm+).
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  // Dropdown de acciones por miembro (móvil card).
  const [openActionsMemberId, setOpenActionsMemberId] = useState<string | null>(
    null,
  );

  // Contador de solicitudes pendientes
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [profileRequestsCount, setProfileRequestsCount] = useState(0);

  useEffect(() => {
    if (!clubId) return;

    const unsubscribe = joinRequestService.watchClubPendingRequests(
      clubId,
      (requests) => {
        // El badge debe contar SOLO lo que esta persona puede resolver. Las
        // solicitudes para dirigir una sección se le ocultan al director de
        // sección (no puede aprobarlas: sería permission-denied), así que
        // contarlas le mostraba "1 pendiente" y una bandeja vacía al entrar.
        // Le pasó al director de Aventureros de GAMA (2026-08-08) con una
        // solicitud para dirigir Guías Mayores.
        setPendingRequestsCount(
          directorOfSection
            ? requests.filter((r) => r.requestedRole !== "section_director")
                .length
            : requests.length,
        );
      },
      (error) => {
        console.error("Error watching pending requests:", error);
      },
    );

    return () => unsubscribe();
  }, [clubId, directorOfSection]);

  // Contador de solicitudes de cambio de perfil pendientes
  useEffect(() => {
    if (!clubId) return;

    const unsubscribe = profileUpdateRequestService.subscribeToPending(
      clubId,
      (requests) => {
        setProfileRequestsCount(requests.length);
      },
    );

    return () => unsubscribe();
  }, [clubId]);

  // Obtener nombre del club
  const getClubName = (clubId: string) => {
    const club = clubs?.find((c) => c.id === clubId);
    return club?.name || "Club";
  };

  // Índice: memberId → Set<section> de cargos activos (incluye cross-section).
  // Permite que al filtrar por p.ej. "aventureros" también se muestren personas
  // cuya edad las ubica en otra sección pero tienen un cargo activo en aventureros.
  const rolesSectionsByMember = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (clubAssignments ?? []).forEach((a) => {
      if (!a.memberId || !a.section) return;
      const set = map.get(a.memberId) ?? new Set<string>();
      set.add(a.section);
      map.set(a.memberId, set);
    });
    return map;
  }, [clubAssignments]);

  // Índice: memberId → lista completa de assignments activos.
  // Se usa para mostrar los cargos EXTRA (los que no son el principal) en la
  // fila del miembro via <MemberExtraRoles>.
  const assignmentsByMember = useMemo(() => {
    const map = new Map<string, MemberRoleAssignment[]>();
    (clubAssignments ?? []).forEach((a) => {
      if (!a.memberId) return;
      const list = map.get(a.memberId) ?? [];
      list.push(a);
      map.set(a.memberId, list);
    });
    return map;
  }, [clubAssignments]);

  // Filtrar miembros
  const filteredMembers = useMemo(() => {
    if (!members) return [];

    const normalize = (s: string) =>
      s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    const q = normalize(searchTerm);
    const currentMonth = new Date().getMonth();

    return members.filter((member) => {
      // Búsqueda ampliada: nombre, email, documento, teléfono,
      // contacto de emergencia. Sin acentos, minúsculas.
      const person = member.person;
      const searchMatch =
        q === "" ||
        (person.fullName && normalize(person.fullName).includes(q)) ||
        (person.email && normalize(person.email).includes(q)) ||
        (person.idDocumentNumber &&
          person.idDocumentNumber
            .replace(/\D/g, "")
            .includes(q.replace(/\D/g, "")) &&
          q.replace(/\D/g, "") !== "") ||
        (person.phone &&
          person.phone.replace(/\D/g, "").includes(q.replace(/\D/g, "")) &&
          q.replace(/\D/g, "") !== "") ||
        (person.emergencyContact?.name &&
          normalize(person.emergencyContact.name).includes(q));

      // Filtro de estado
      const statusMatch =
        statusFilter === "all" || member.status === statusFilter;

      // Filtro de rol
      const roleMatch = roleFilter === "all" || member.role === roleFilter;

      // Filtro por sección/tipo de club — incluye tres fuentes:
      //  1. section (por edad)
      //  2. staffSection (cargo principal legacy)
      //  3. cualquier memberRole activo en esa sección (multi-cargo cruzado)
      const roleSections = rolesSectionsByMember.get(member.id);
      const sectionMatch =
        sectionFilter === "all" ||
        member.section === sectionFilter ||
        member.staffSection === sectionFilter ||
        roleSections?.has(sectionFilter) === true;

      // Filtro por documentación
      let docMatch = true;
      if (docFilter !== "all") {
        const hasPhoto = !!person.photoUrl;
        const hasDoc = !!person.documentFrontUrl && !!person.documentBackUrl;
        const hasEps = !!person.epsDocumentUrl || !!person.healthInsurance;
        const isComplete = hasPhoto && hasDoc && hasEps;
        docMatch =
          docFilter === "no-photo"
            ? !hasPhoto
            : docFilter === "no-doc"
              ? !hasDoc
              : docFilter === "no-eps"
                ? !hasEps
                : docFilter === "complete"
                  ? isComplete
                  : /* incomplete */ !isComplete;
      }

      // Filtro por cumpleaños este mes
      let birthdayMatch = true;
      if (birthdayThisMonth) {
        const bd = person.birthDate as
          | { toDate?: () => Date }
          | Date
          | null
          | undefined;
        if (!bd) {
          birthdayMatch = false;
        } else {
          const d =
            typeof bd === "object" && "toDate" in bd && bd.toDate
              ? bd.toDate()
              : new Date(bd as unknown as string | number | Date);
          birthdayMatch = d.getMonth() === currentMonth;
        }
      }

      // Filtro "solo staff" (cualquier rol distinto a member)
      const staffMatch = !staffOnly || member.role !== "member";

      return (
        searchMatch &&
        statusMatch &&
        roleMatch &&
        sectionMatch &&
        docMatch &&
        birthdayMatch &&
        staffMatch
      );
    });
  }, [
    members,
    searchTerm,
    statusFilter,
    roleFilter,
    sectionFilter,
    docFilter,
    birthdayThisMonth,
    staffOnly,
    rolesSectionsByMember,
  ]);

  // Ordenar miembros según selección
  const sortedMembers = useMemo(() => {
    const arr = [...filteredMembers];
    const dir = sortDir === "asc" ? 1 : -1;
    const getAge = (m: (typeof arr)[number]): number => {
      const bd = m.person.birthDate as
        | { toDate?: () => Date }
        | Date
        | null
        | undefined;
      if (!bd) return -1;
      const d =
        typeof bd === "object" && "toDate" in bd && bd.toDate
          ? bd.toDate()
          : new Date(bd as unknown as string | number | Date);
      const today = new Date();
      let age = today.getFullYear() - d.getFullYear();
      const mo = today.getMonth() - d.getMonth();
      if (mo < 0 || (mo === 0 && today.getDate() < d.getDate())) age--;
      return age;
    };
    const getDocPct = (m: (typeof arr)[number]): number => {
      const p = m.person;
      const checks = [
        !!p.photoUrl,
        !!p.documentFrontUrl,
        !!p.documentBackUrl,
        !!p.epsDocumentUrl || !!p.healthInsurance,
        !!p.emergencyContact?.phone,
        !!p.address,
      ];
      return checks.filter(Boolean).length / checks.length;
    };
    const getRecent = (m: (typeof arr)[number]): number => {
      const ca = (m as unknown as { createdAt?: { toDate?: () => Date } })
        .createdAt;
      if (!ca) return 0;
      if (typeof ca === "object" && "toDate" in ca && ca.toDate) {
        return ca.toDate().getTime();
      }
      return new Date(ca as unknown as string | number | Date).getTime();
    };
    arr.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (
            dir *
            (a.person.fullName || "").localeCompare(
              b.person.fullName || "",
              "es",
              { sensitivity: "base" },
            )
          );
        case "age":
          return dir * (getAge(a) - getAge(b));
        case "docPct":
          return dir * (getDocPct(a) - getDocPct(b));
        case "recent":
          return dir * (getRecent(a) - getRecent(b));
      }
    });
    return arr;
  }, [filteredMembers, sortBy, sortDir]);

  const handleEdit = (member: Member) => {
    setSelectedMember(member);
    setShowMemberModal(true);
  };

  const handleView = (member: Member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const handleDelete = async () => {
    if (!memberToDelete) return;

    try {
      if (hardDeleteMode) {
        // BUG-002: Validación extra: nombre debe coincidir exactamente
        const expected = memberToDelete.person.fullName?.trim() ?? "";
        if (hardDeleteConfirmName.trim() !== expected) {
          return;
        }
        await hardDeleteMutation.mutateAsync(memberToDelete.id);
      } else {
        await deleteMutation.mutateAsync(memberToDelete.id);
      }
      setMemberToDelete(null);
      setHardDeleteMode(false);
      setHardDeleteConfirmName("");
    } catch (error) {
      console.error("Error al eliminar/desactivar miembro:", error);
    }
  };

  const handleCloseMemberModal = () => {
    setShowMemberModal(false);
    setSelectedMember(null);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setRoleFilter("all");
    setSectionFilter(directorOfSection ?? "all");
    setDocFilter("all");
    setBirthdayThisMonth(false);
    setStaffOnly(false);
  };

  const hasActiveFilters =
    searchTerm !== "" ||
    statusFilter !== "all" ||
    roleFilter !== "all" ||
    sectionFilter !== (directorOfSection ?? "all") ||
    docFilter !== "all" ||
    birthdayThisMonth ||
    staffOnly;

  const calculateAge = (
    birthDate: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!birthDate) return "-";
    const date =
      typeof birthDate === "object" && "toDate" in birthDate && birthDate.toDate
        ? birthDate.toDate()
        : new Date(birthDate as unknown as string | number | Date);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      age--;
    }
    return `${age} años`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Aviso informativo: director de sección ve todos los miembros del
          club; ya no filtramos por sección — el usuario usa los badges y el
          filtro de sección para acotar visualmente. */}
      {directorOfSection && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">
              Eres director/a de {CLUB_TYPE_LABELS[directorOfSection]}
            </p>
            <p className="text-blue-800/90 text-xs mt-0.5">
              Puedes ver y editar a todos los miembros del club. Usa el filtro
              de sección para ver solo los tuyos.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Users className="w-5 h-5 text-white" />
            </div>
            Gestión de Miembros
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px] text-sm sm:text-base">
            Administra los miembros de tu club
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Botón de solicitudes de unión pendientes */}
          <Link
            to="/members/requests"
            aria-label="Solicitudes pendientes"
            className="relative flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300"
          >
            <Inbox className="w-4 h-4" />
            <span className="hidden sm:inline">Solicitudes</span>
            {pendingRequestsCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                {pendingRequestsCount}
              </span>
            )}
          </Link>
          {/* Botón de cambios de perfil pendientes — temporalmente oculto */}
          {false && (
            <Link
              to="/members/profile-requests"
              aria-label="Cambios de perfil pendientes"
              className="relative flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300"
            >
              <Edit className="w-4 h-4" />
              <span className="hidden sm:inline">Cambios</span>
              {profileRequestsCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-amber-500 rounded-full animate-pulse">
                  {profileRequestsCount}
                </span>
              )}
            </Link>
          )}
          <Can permission="members.write">
            <Button
              onClick={() => setShowMemberModal(true)}
              aria-label="Nuevo miembro"
              className="flex items-center gap-2 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Nuevo Miembro</span>
              <span className="sm:hidden">Nuevo</span>
            </Button>
          </Can>
        </div>
      </div>

      {/* Filtros */}
      <Card className="border-0 shadow-md">
        <CardContent className="py-4 space-y-4">
          {/* Barra de presets rápidos */}
          <div className="flex flex-wrap gap-2">
            <QuickPreset
              icon={Users}
              label={`Todos (${members?.length ?? 0})`}
              active={!hasActiveFilters}
              onClick={clearFilters}
            />
            <QuickPreset
              icon={Award}
              label="Directiva"
              active={staffOnly}
              onClick={() => {
                clearFilters();
                setStaffOnly(true);
              }}
            />
            <QuickPreset
              icon={Camera}
              label="Sin foto"
              active={docFilter === "no-photo"}
              onClick={() => {
                clearFilters();
                setDocFilter("no-photo");
              }}
            />
            <QuickPreset
              icon={FileWarning}
              label="Documentación incompleta"
              active={docFilter === "incomplete"}
              onClick={() => {
                clearFilters();
                setDocFilter("incomplete");
              }}
            />
            <QuickPreset
              icon={Cake}
              label="Cumpleaños este mes"
              active={birthdayThisMonth}
              onClick={() => {
                clearFilters();
                setBirthdayThisMonth(true);
              }}
            />
            <QuickPreset
              icon={CheckCircle2}
              label="Al día"
              active={docFilter === "complete"}
              onClick={() => {
                clearFilters();
                setDocFilter("complete");
              }}
            />
          </div>

          {/* Búsqueda: ancho completo */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, email, documento, teléfono o contacto de emergencia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
              className="pl-11 border-2 border-gray-200 focus:border-primary-500 rounded-xl"
            />
          </div>

          {/* Toggle filtros (solo mobile) */}
          <div className="flex sm:hidden">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowFiltersMobile((v) => !v)}
              className="flex items-center gap-2 w-full justify-center"
              aria-expanded={showFiltersMobile}
              aria-controls="members-filters-panel"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtros
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-primary-100 text-primary-700">
                  {(statusFilter !== "all" ? 1 : 0) +
                    (roleFilter !== "all" ? 1 : 0) +
                    (sectionFilter !== (directorOfSection ?? "all") ? 1 : 0) +
                    (docFilter !== "all" ? 1 : 0) +
                    (birthdayThisMonth ? 1 : 0) +
                    (staffOnly ? 1 : 0)}
                </span>
              )}
              <ChevronDown
                className={`w-4 h-4 ml-auto transition-transform ${
                  showFiltersMobile ? "rotate-180" : ""
                }`}
              />
            </Button>
          </div>

          {/* Fila de filtros: horizontal en desktop, colapsable en mobile */}
          <div
            id="members-filters-panel"
            className={`${
              showFiltersMobile ? "grid" : "hidden"
            } sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2`}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-2 border-gray-200 rounded-xl"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="pending">Pendientes</option>
            </Select>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border-2 border-gray-200 rounded-xl"
            >
              <option value="all">Todos los roles</option>
              <option value="director">Director</option>
              <option value="subdirector">Subdirector</option>
              <option value="secretario">Secretario</option>
              <option value="tesorero">Tesorero</option>
              <option value="anciano">Anciano</option>
              <option value="consejero">Consejero</option>
              <option value="instructor">Instructor</option>
              <option value="member">Miembro</option>
            </Select>
            <Select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="border-2 border-gray-200 rounded-xl"
              title="Filtrar por sección"
            >
              <option value="all">Todas las secciones</option>
              <option value="aventureros">Aventureros</option>
              <option value="conquistadores">Conquistadores</option>
              <option value="guias_mayores">Guías Mayores</option>
            </Select>
            <Select
              value={docFilter}
              onChange={(e) => setDocFilter(e.target.value as typeof docFilter)}
              className="border-2 border-gray-200 rounded-xl"
              title="Filtrar por documentación"
            >
              <option value="all">Toda documentación</option>
              <option value="no-photo">Sin foto</option>
              <option value="no-doc">Sin documento</option>
              <option value="no-eps">Sin EPS</option>
              <option value="incomplete">Incompleta</option>
              <option value="complete">Completa</option>
            </Select>
            <Select
              value={`${sortBy}:${sortDir}`}
              onChange={(e) => {
                const [b, d] = e.target.value.split(":") as [
                  typeof sortBy,
                  typeof sortDir,
                ];
                setSortBy(b);
                setSortDir(d);
              }}
              className="border-2 border-gray-200 rounded-xl"
              title="Ordenar"
            >
              <option value="name:asc">Nombre (A → Z)</option>
              <option value="name:desc">Nombre (Z → A)</option>
              <option value="age:asc">Edad (menor a mayor)</option>
              <option value="age:desc">Edad (mayor a menor)</option>
              <option value="docPct:asc">Documentación (menos → más)</option>
              <option value="docPct:desc">Documentación (más → menos)</option>
              <option value="recent:desc">Más recientes</option>
              <option value="recent:asc">Más antiguos</option>
            </Select>
          </div>

          {/* Chips de filtros activos + contador + limpiar */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
              <span className="text-xs font-medium text-gray-500 mr-1">
                Filtros activos:
              </span>
              {searchTerm !== "" && (
                <FilterChip
                  label={`Búsqueda: "${searchTerm}"`}
                  onRemove={() => setSearchTerm("")}
                />
              )}
              {statusFilter !== "all" && (
                <FilterChip
                  label={`Estado: ${statusLabels[statusFilter] || statusFilter}`}
                  onRemove={() => setStatusFilter("all")}
                />
              )}
              {roleFilter !== "all" && (
                <FilterChip
                  label={`Rol: ${MEMBER_ROLE_LABELS[roleFilter as keyof typeof MEMBER_ROLE_LABELS] || roleFilter}`}
                  onRemove={() => setRoleFilter("all")}
                />
              )}
              {sectionFilter !== (directorOfSection ?? "all") && (
                <FilterChip
                  label={`Sección: ${CLUB_TYPE_LABELS[sectionFilter as ClubType] || sectionFilter}`}
                  onRemove={() => setSectionFilter(directorOfSection ?? "all")}
                />
              )}
              {docFilter !== "all" && (
                <FilterChip
                  label={`Documentación: ${DOC_FILTER_LABELS[docFilter]}`}
                  onRemove={() => setDocFilter("all")}
                />
              )}
              {birthdayThisMonth && (
                <FilterChip
                  label="Cumpleaños este mes"
                  onRemove={() => setBirthdayThisMonth(false)}
                />
              )}
              {staffOnly && (
                <FilterChip
                  label="Solo directiva"
                  onRemove={() => setStaffOnly(false)}
                />
              )}
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium ml-auto"
              >
                <RotateCcw className="w-3 h-3" />
                Limpiar todo
              </button>
            </div>
          )}

          {/* Contador de resultados */}
          <p className="text-xs text-gray-500">
            <span className="font-semibold text-gray-700">
              {sortedMembers.length}
            </span>{" "}
            de {members?.length ?? 0} miembros
            {hasActiveFilters && " · filtrados"}
          </p>
        </CardContent>
      </Card>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="!p-4 border-0 shadow-md group hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-blue-50/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total</p>
              <p className="text-3xl font-bold text-gray-900">
                {members?.length || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
        </Card>
        <Card className="!p-4 border-0 shadow-md group hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-green-50/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Activos</p>
              <p className="text-3xl font-bold text-green-600">
                {members?.filter((m) => m.status === "active").length || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform">
              <UserCheck className="w-5 h-5 text-white" />
            </div>
          </div>
        </Card>
        <Card className="!p-4 border-0 shadow-md group hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-indigo-50/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Staff</p>
              <p className="text-3xl font-bold text-indigo-600">
                {members?.filter((m) => m.role !== "member").length || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform">
              <UserCheck className="w-5 h-5 text-white" />
            </div>
          </div>
        </Card>
        <Card className="!p-4 border-0 shadow-md group hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-purple-50/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Miembros</p>
              <p className="text-3xl font-bold text-purple-600">
                {members?.filter((m) => m.role === "member").length || 0}
              </p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de miembros */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-100">
          <CardTitle className="flex items-center justify-between">
            <span>Miembros ({sortedMembers.length})</span>
            {hasActiveFilters && (
              <span className="text-sm font-normal text-gray-500">
                Filtrado de {members?.length || 0} total
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {sortedMembers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Users className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-gray-600 font-medium">
                {hasActiveFilters
                  ? "No se encontraron miembros con los filtros aplicados"
                  : "No hay miembros registrados"}
              </p>
              {!hasActiveFilters && (
                <Button
                  onClick={() => setShowMemberModal(true)}
                  className="mt-4 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25"
                >
                  Registrar primer miembro
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Vista mobile: cards */}
              <div className="md:hidden space-y-3 -mx-2">
                {sortedMembers.map((member) => {
                  const sec = member.staffSection ?? member.section;
                  const isMember = member.role === "member";
                  const cross =
                    member.staffSection &&
                    member.staffSection !== member.section;
                  const labels = CLUB_TYPE_LABELS as Record<string, string>;
                  const memberAssignments =
                    assignmentsByMember.get(member.id) ?? [];
                  return (
                    <div
                      key={member.id}
                      onClick={() => handleView(member)}
                      className="relative bg-white border border-gray-200 rounded-2xl p-3 shadow-sm active:scale-[0.99] transition-transform"
                    >
                      <div className="flex items-start gap-3">
                        {member.person.photoUrl ? (
                          <img
                            src={member.person.photoUrl}
                            alt={member.person.fullName}
                            className="w-12 h-12 rounded-xl object-cover shadow-md shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium shadow-md shadow-primary-500/25 shrink-0">
                            {member.person.fullName?.charAt(0) || "?"}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {member.person.fullName}
                              </p>
                              {member.person.email && (
                                <p className="text-xs text-gray-500 truncate">
                                  {member.person.email}
                                </p>
                              )}
                            </div>
                            <div
                              className="relative shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  setOpenActionsMemberId(
                                    openActionsMemberId === member.id
                                      ? null
                                      : member.id,
                                  )
                                }
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
                                aria-label="Acciones"
                                aria-expanded={
                                  openActionsMemberId === member.id
                                }
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              {openActionsMemberId === member.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setOpenActionsMemberId(null)}
                                  />
                                  <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px]">
                                    <button
                                      onClick={() => {
                                        setOpenActionsMemberId(null);
                                        handleView(member);
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                      <Eye className="w-4 h-4" />
                                      Ver detalles
                                    </button>
                                    <Can
                                      permission="members.write"
                                      clubId={member.clubId}
                                    >
                                      <button
                                        onClick={() => {
                                          setOpenActionsMemberId(null);
                                          handleEdit(member);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <Edit className="w-4 h-4" />
                                        Editar
                                      </button>
                                    </Can>
                                    {member.status === "inactive" ? (
                                      <Can
                                        permission="members.write"
                                        clubId={member.clubId}
                                      >
                                        <button
                                          onClick={() => {
                                            if (!profile?.id) return;
                                            setOpenActionsMemberId(null);
                                            reactivateMutation.mutate({
                                              id: member.id,
                                              userId: profile.id,
                                            });
                                          }}
                                          disabled={
                                            reactivateMutation.isPending
                                          }
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                                        >
                                          <RotateCcw className="w-4 h-4" />
                                          Reactivar
                                        </button>
                                      </Can>
                                    ) : (
                                      <Can
                                        permission="members.delete"
                                        clubId={member.clubId}
                                      >
                                        <button
                                          onClick={() => {
                                            setOpenActionsMemberId(null);
                                            setMemberToDelete(member);
                                          }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                          Desactivar
                                        </button>
                                      </Can>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {isMember && !member.staffSection ? (
                              member.section ? (
                                <SectionBadge
                                  section={member.section}
                                  variant="soft"
                                  size="xs"
                                />
                              ) : null
                            ) : (
                              <RoleBadge
                                role={member.role}
                                section={sec ?? undefined}
                                showSection={!!sec}
                                size="xs"
                              />
                            )}
                            {member.clase && (
                              <Badge variant="info" size="xs">
                                {MEMBER_CLASS_LABELS[member.clase]}
                              </Badge>
                            )}
                            <Badge className={statusColors[member.status]}>
                              {statusLabels[member.status] || member.status}
                            </Badge>
                          </div>
                          <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {getClubName(member.clubId)}
                            </span>
                            <span>
                              {calculateAge(member.person.birthDate)} años
                            </span>
                          </div>
                          {cross && member.section && (
                            <span className="block text-[10px] text-gray-400 mt-1">
                              {labels[member.section] ?? member.section} por
                              edad
                            </span>
                          )}
                          {memberAssignments.length > 0 && (
                            <div className="mt-1">
                              <MemberExtraRoles
                                assignments={memberAssignments}
                                principalRole={member.role}
                                principalSection={sec ?? undefined}
                                principalClubId={member.clubId}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Vista desktop: tabla */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Foto
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Nombre
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Rol
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Clase
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Edad
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Club
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Estado
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedMembers.map((member) => (
                      <tr
                        key={member.id}
                        className="hover:bg-gray-50/80 cursor-pointer transition-colors duration-150"
                        onClick={() => handleView(member)}
                      >
                        <td className="py-3 px-4">
                          {member.person.photoUrl ? (
                            <img
                              src={member.person.photoUrl}
                              alt={member.person.fullName}
                              className="w-10 h-10 rounded-xl object-cover shadow-md"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium shadow-md shadow-primary-500/25">
                              {member.person.fullName?.charAt(0) || "?"}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900">
                                {member.person.fullName}
                              </p>
                              {(() => {
                                // Chip informativo cuando el miembro aparece SOLO
                                // por un cargo cruzado en la sección filtrada.
                                if (sectionFilter === "all") return null;
                                const bySection =
                                  member.section === sectionFilter;
                                const byStaff =
                                  member.staffSection === sectionFilter;
                                if (bySection || byStaff) return null;
                                const roleSections = rolesSectionsByMember.get(
                                  member.id,
                                );
                                if (!roleSections?.has(sectionFilter))
                                  return null;
                                const labels = CLUB_TYPE_LABELS as Record<
                                  string,
                                  string
                                >;
                                return (
                                  <Badge variant="info" size="xs">
                                    Cargo en{" "}
                                    {labels[sectionFilter] ?? sectionFilter}
                                  </Badge>
                                );
                              })()}
                            </div>
                            {member.person.email && (
                              <p className="text-sm text-gray-500">
                                {member.person.email}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            const sec = member.staffSection ?? member.section;
                            const isMember = member.role === "member";
                            const cross =
                              member.staffSection &&
                              member.staffSection !== member.section;
                            const labels = CLUB_TYPE_LABELS as Record<
                              string,
                              string
                            >;
                            const memberAssignments =
                              assignmentsByMember.get(member.id) ?? [];
                            return (
                              <>
                                {isMember && !member.staffSection ? (
                                  member.section ? (
                                    <SectionBadge
                                      section={member.section}
                                      variant="soft"
                                      size="xs"
                                    />
                                  ) : (
                                    <span className="text-sm text-gray-500">
                                      -
                                    </span>
                                  )
                                ) : (
                                  <RoleBadge
                                    role={member.role}
                                    section={sec ?? undefined}
                                    showSection={!!sec}
                                    size="xs"
                                  />
                                )}
                                {cross && member.section && (
                                  <span className="block text-[10px] text-gray-400 mt-0.5">
                                    {labels[member.section] ?? member.section}{" "}
                                    por edad
                                  </span>
                                )}
                                <MemberExtraRoles
                                  assignments={memberAssignments}
                                  principalRole={member.role}
                                  principalSection={sec ?? undefined}
                                  principalClubId={member.clubId}
                                />
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {member.clase
                            ? MEMBER_CLASS_LABELS[member.clase]
                            : "-"}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {calculateAge(member.person.birthDate)}
                        </td>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-1 text-sm text-gray-600">
                            <Building2 className="w-3 h-3" />
                            {getClubName(member.clubId)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={statusColors[member.status]}>
                            {statusLabels[member.status] || member.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div
                            className="flex items-center justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => handleView(member)}
                              className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors duration-200"
                              title="Ver detalles"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <Can
                              permission="members.write"
                              clubId={member.clubId}
                            >
                              <button
                                onClick={() => handleEdit(member)}
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors duration-200"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </Can>
                            {member.status === "inactive" ? (
                              <Can
                                permission="members.write"
                                clubId={member.clubId}
                              >
                                <button
                                  onClick={() => {
                                    if (!profile?.id) return;
                                    reactivateMutation.mutate({
                                      id: member.id,
                                      userId: profile.id,
                                    });
                                  }}
                                  disabled={reactivateMutation.isPending}
                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-colors duration-200 disabled:opacity-50"
                                  title="Reactivar"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              </Can>
                            ) : (
                              <Can
                                permission="members.delete"
                                clubId={member.clubId}
                              >
                                <button
                                  onClick={() => setMemberToDelete(member)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200"
                                  title="Desactivar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </Can>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {showMemberModal && (
        <MemberModal
          isOpen={showMemberModal}
          onClose={handleCloseMemberModal}
          member={selectedMember}
        />
      )}

      {/* Modal de Detalles */}
      {selectedMember && showDetailModal && (
        <MemberDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          onEdit={() => {
            setShowDetailModal(false);
            setShowMemberModal(true);
          }}
        />
      )}

      {/* Modal de confirmación de eliminación */}
      {memberToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
              {hardDeleteMode
                ? "Eliminar permanentemente"
                : "Confirmar desactivación"}
            </h3>
            <p className="text-gray-600 mb-4 text-center">
              {hardDeleteMode ? (
                <>
                  Vas a eliminar <strong>permanentemente</strong> a{" "}
                  <strong>{memberToDelete.person.fullName}</strong> de este
                  club, junto con su progreso de clases, logros e inscripciones
                  a eventos del club. Esta acción es{" "}
                  <strong>irreversible</strong>.
                </>
              ) : (
                <>
                  ¿Estás seguro de que deseas desactivar a{" "}
                  <strong>{memberToDelete.person.fullName}</strong>? El miembro
                  quedará inactivo en el club.
                </>
              )}
            </p>

            {/* BUG-002: Toggle hard delete */}
            <label className="flex items-start gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={hardDeleteMode}
                onChange={(e) => {
                  setHardDeleteMode(e.target.checked);
                  setHardDeleteConfirmName("");
                }}
                className="mt-0.5"
              />
              <span className="text-sm text-red-800">
                Eliminar permanentemente (irreversible). Borra al miembro y sus
                datos relacionados en este club.
              </span>
            </label>

            {hardDeleteMode && (
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-1">
                  Para confirmar, escribe el nombre completo:{" "}
                  <strong>{memberToDelete.person.fullName}</strong>
                </label>
                <input
                  type="text"
                  value={hardDeleteConfirmName}
                  onChange={(e) => setHardDeleteConfirmName(e.target.value)}
                  placeholder={memberToDelete.person.fullName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
              </div>
            )}

            <div className="flex justify-center gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setMemberToDelete(null);
                  setHardDeleteMode(false);
                  setHardDeleteConfirmName("");
                }}
                className="rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={
                  deleteMutation.isPending ||
                  hardDeleteMutation.isPending ||
                  (hardDeleteMode &&
                    hardDeleteConfirmName.trim() !==
                      (memberToDelete.person.fullName?.trim() ?? ""))
                }
                className="rounded-xl bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/25"
              >
                {deleteMutation.isPending || hardDeleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {hardDeleteMode ? "Eliminando..." : "Desactivando..."}
                  </>
                ) : hardDeleteMode ? (
                  "Eliminar permanentemente"
                ) : (
                  "Desactivar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL - Decide qué vista mostrar
// ============================================
export default function MembersPage() {
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();

  // Unión: Solo estadísticas agregadas
  if (isUnionAdmin) {
    return <UnionMembersView />;
  }

  // Asociación: Lista completa de miembros con filtros
  if (isAssociationAdmin) {
    return <AssociationMembersView />;
  }

  // Club Director: Gestión completa
  return <ClubMembersView />;
}
